import React, { useState, useEffect, useCallback } from "react";
import FadeTransition from "../../../../../../components/FadeTransition";
import LoadingCircle from "../../../../../../components/LoadingCircle";
import ListPageHeader from "../../../../../../components/Tables/ListPageControls/ListPageHeader";
import PaginationContainer from "./PaginationContainer";
import BuildingTable from "./Table/BuildingTable";
import useTableSorting from "../../../../../../hooks/useTableSorting";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchBuildings,
  selectLoading,
  selectAllBuildings,
  fetchAllBuildings,
} from "../../../../../../redux/buildings/buildingsSlice";
import useDebounce from "../../../../../../hooks/useDebounce";
import {
  selectCurrentPage,
  selectRowsPerPage,
  setCurrentPage,
} from "../../../../../../redux/buildings/buildingsPaginationSlice";
import useExport from "../../../../../../hooks/useExport";
import AlertSnackbar from "../../../../../../components/Notifications/AlertSnackbar";
import useAddBuilding from "../../hooks/useBuildingAdd";
import useBuildingDelete from "../../hooks/useBuildingDelete";
import {
  clearSelectedBuilding,
  setSelectedBuilding,
} from "../../../../../../redux/buildings/selectedBuildingSlice";
import BuildingViewModal from "../Modal/ViewModal";
import { clearSelectedBuildingContact } from "../../../../../../redux/buildings/selectedBuildingContactSlice";
import { clearSelectedBuildingDevices } from "../../../../../../redux/buildings/selectedBuildingDevicesSlice";
import {
  fetchTaxDetails,
  fetchSalesPersons,
} from "../../../Customer/services/customerLookupService";
import { selectAllCustomers } from "../../../../../../redux/customers/customerSlice";
import { selectAllCompanies } from "../../../../../../redux/mgmtCo/managementSlice";
import { fetchAllManagementCompanies } from "../../../../../../redux/mgmtCo/managementSlice";
import { fetchAllCustomers } from "../../../../../../redux/customers/customerSlice";
import API_BASE_URL from "../../../../../../Constants";

const ListPage = () => {
  const dispatch = useDispatch();
  const loading = useSelector(selectLoading);
  const currentPage = useSelector(selectCurrentPage);
  const rowsPerPage = useSelector(selectRowsPerPage);
  const allBuildings = useSelector(selectAllBuildings);
  const allCustomers = useSelector(selectAllCustomers);
  const allManagementCompanies = useSelector(selectAllCompanies);
  const [allAgencies, setAllAgencies] = useState([]);

  const [dropdownLists, setDropdownLists] = useState({
    allTaxDetails: [],
    allSalesPersons: [],
    allAgencies: [],
  });

  useEffect(() => {
    async function fetchDropdownData() {
      try {
        const taxDetails = await fetchTaxDetails();
        const salesPersons = await fetchSalesPersons();
        const agencies = await fetch(
          `${API_BASE_URL}/api/inspections/agency/agencies`
        );
        const data = await agencies.json();
        setDropdownLists({
          allTaxDetails: Array.isArray(taxDetails) ? taxDetails : [],
          allSalesPersons: Array.isArray(salesPersons) ? salesPersons : [],
          allAgencies: Array.isArray(data) ? data : [],
        });
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
      }
    }
    async function fetchCustomers() {
      try {
        const customers = await dispatch(fetchAllCustomers());
      } catch (error) {
        console.error("Error fetching customers:", error);
      }
    }
    async function fetchManagementCompanies() {
      try {
        const managementCompanies = await dispatch(
          fetchAllManagementCompanies()
        );
      } catch (error) {
        console.error("Error fetching management companies:", error);
      }
    }
    async function fetchAllAgencies() {
      try {
        const agencies = await fetch(
          `${API_BASE_URL}/api/inspections/agency/agencies`
        );
        const data = await agencies.json();
        setAllAgencies(data);
      } catch (error) {
        console.error("Error fetching agencies:", error);
      }
    }
    fetchDropdownData();
    fetchCustomers();
    fetchManagementCompanies();
    fetchAllAgencies();
  }, []);

  // local states
  const [listExpanded, setListExpanded] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [isEditMode, setIsEditMode] = useState(false);

  const { sortField, order, handleSort } = useTableSorting("buildingID", "asc");

  const [validationErrors, setValidationErrors] = useState({});

  // Snackbar handler
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleAddSuccess = () => {
    setSnackbar({
      open: true,
      message: "Building added successfully.",
      severity: "success",
    });
  };

  // Custom hooks for create/delete
  const createNewBuilding = useAddBuilding();
  const deleteSelected = useBuildingDelete();

  // Export hook
  const { exportToCSV } = useExport();

  // Export handler for entire building list
  const handleExport = () => {
    exportToCSV(allBuildings, "buildings.csv");
  };

  const handleOpen = () => {
    setValidationErrors({});
    setIsViewOpen(true);
  };

  const handleAddAndView = async () => {
    try {
      const newBuilding = await createNewBuilding();
      if (newBuilding) {
        dispatch(setSelectedBuilding(newBuilding));
        const result = await dispatch(
          fetchBuildings({
            currentPage,
            rowsPerPage,
            searchQuery: "",
            sortField,
            sortOrder: order,
          })
        );
        const newTotalPages = result.payload.totalPages;
        if (currentPage !== newTotalPages) {
          dispatch(setCurrentPage(newTotalPages));
        }
      }
      setIsViewOpen(true);
      setIsEditMode(true);
      handleAddSuccess();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: "error",
      });
    }
  };

  const handleOpenAddFromView = () => {
    handleCloseModal();
    handleAddAndView();
  };

  const handleDeleteSelected = async () => {
    try {
      await deleteSelected();
      setSnackbar({
        open: true,
        message: "Building deleted successfully.",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: "error",
      });
    }
  };

  const updateList = useCallback(() => {
    try {
      // update dependent information here if needed
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: "error",
      });
    }
  }, []);

  // Debounce search query
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  useEffect(() => {
    dispatch(
      fetchBuildings({
        currentPage,
        rowsPerPage,
        searchQuery: debouncedSearchQuery,
        sortField,
        sortOrder: order,
      })
    );
  }, [
    dispatch,
    currentPage,
    rowsPerPage,
    debouncedSearchQuery,
    sortField,
    order,
  ]);

  useEffect(() => {
    if (!allBuildings || allBuildings.length === 0) {
      dispatch(fetchAllBuildings());
    }
  }, [dispatch, allBuildings]);

  const handleCloseModal = () => {
    setIsEditMode(false);
    setIsViewOpen(false);
    setValidationErrors({});
    dispatch(clearSelectedBuildingContact());
    dispatch(clearSelectedBuildingDevices());
    dispatch(clearSelectedBuilding());
  };

  return (
    <div className="flex flex-col h-full min-h-0 relative">
      <div className="flex-1 min-h-0 overflow-auto relative bg-gray-50">
        <FadeTransition show={loading}>
          <div
            className={`transition-all duration-300 ${
              listExpanded ? "w-full" : "w-11/12"
            } mx-auto px-10`}
          >
            <ListPageHeader
              title="Buildings: "
              onAdd={handleAddAndView}
              toggleFilterModal={() => {}}
              deleteSelected={handleDeleteSelected}
              onSearch={(value) => setSearchQuery(value)}
              isExpanded={listExpanded}
              setIsExpanded={() => setListExpanded(!listExpanded)}
              onExport={handleExport}
            />
            <BuildingTable
              handleOpenViewModal={handleOpen}
              expanded={listExpanded}
              sortField={sortField}
              order={order}
              handleSort={handleSort}
              customers={allCustomers}
              managementCompanies={allManagementCompanies}
              agencies={allAgencies}
            />
          </div>
        </FadeTransition>
      </div>
      <PaginationContainer />
      {/* Modals */}
      <BuildingViewModal
        open={isViewOpen}
        handleClose={handleCloseModal}
        handleOpenAddFromView={handleOpenAddFromView}
        updateList={updateList}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        dropdownLists={dropdownLists}
      />

      <AlertSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
      />
    </div>
  );
};

export default ListPage;
